<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, ref } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);

const emptyRuntime = {
  actions: { finance: {} },
  data: ref({}),
  error: ref(''),
  loading: ref(false),
  saving: ref(false),
  selectedId: ref(''),
};

const realData = inject('imudeiRealData', null) || emptyRuntime;
const model = useImudeiRuntimeModel({
  realData,
  module: computed(() => 'finance'),
  screen: computed(() => 'create'),
});

const searchQuery = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const valueAt = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const found = paths
    .map(path => valueAt(value, path))
    .find(item => item !== undefined && item !== null && item !== '');
  return found ?? fallback;
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const formatMoney = value => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    const formatted = firstValue(value, ['formatted', 'display', 'label']);
    if (formatted) return formatted;
    return formatMoney(value.amount ?? value.value ?? value.amount_cents);
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency',
  }).format(amount);
};
const formatDate = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeZone: 'UTC',
  }).format(parsed);
};
const formatDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(parsed);
};
const statusTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/pago|conclu|concili|confirm/.test(status)) {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (/cancel|atras|erro/.test(status)) return 'bg-rose-50 text-rose-700';
  if (/pend|aguard|prev/.test(status)) return 'bg-orange-50 text-orange-700';
  return 'bg-violet-50 text-[#5a24c7]';
};

const creationChoices = [
  {
    description: 'Gere lançamentos a partir de um contrato ou proposta de venda.',
    icon: 'i-lucide-building-2',
    title: 'Vincular a uma venda',
  },
  {
    description: 'Registre valores relacionados a um atendimento de pós-venda.',
    icon: 'i-lucide-file-text',
    title: 'Vincular a um pós-venda',
  },
  {
    description: 'Crie um lançamento avulso, sem vínculo com venda ou pós-venda.',
    icon: 'i-lucide-plus',
    title: 'Lançamento independente',
  },
];
const stages = [
  'Tipo',
  'Identificação',
  'Valores',
  'Comprovantes',
  'Revisão',
];

const canCreate = computed(() => Boolean(model.canCreate.value));
const runtimeFields = computed(() => model.fields.value || []);
const unavailableMessage = computed(
  () =>
    model.unavailableMessage.value ||
    'A API atual não disponibiliza criação de lançamento financeiro por esta rota.'
);
const error = computed(() => realData.error?.value || model.localError.value || '');
const loading = computed(() => Boolean(realData.loading?.value));
const saving = computed(() => Boolean(realData.saving?.value));
const records = computed(() => model.records.value || []);
const existingLaunches = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
  const rows = records.value.map(record => {
    const source = record.source || {};
    const address = source.endereco || source.parcela?.endereco || {};
    const property = [
      address.logradouro,
      address.numero,
      address.unidade,
      address.bairro,
    ]
      .filter(Boolean)
      .join(' · ');
    const manager = firstValue(source, [
      'manager.name',
      'dados_processo.manager.name',
      'responsible.name',
    ]);
    return {
      amount: record.amount || formatMoney(firstValue(source, ['valor_parcela', 'amount'])),
      id: String(record.id || source.id || ''),
      manager,
      property,
      reference: firstValue(source, ['codigo', 'processo.codigo', 'processo_id']),
      scheduledAt: firstValue(source, ['data_prevista', 'due_at', 'due_date']),
      status: record.status || firstValue(source, ['status.label', 'status']),
      title: record.title || property || 'Lançamento financeiro',
    };
  });

  if (!query) return rows.slice(0, 5);
  return rows
    .filter(row =>
      `${row.id} ${row.reference} ${row.title} ${row.property} ${row.manager}`
        .toLocaleLowerCase('pt-BR')
        .includes(query)
    )
    .slice(0, 5);
});
const auditEvents = computed(() =>
  asArray(realData.data.value?.history)
    .map((event, index) => ({
      actor: firstValue(event, ['ator.name', 'actor.name', 'user.name'], 'Sistema'),
      date: firstValue(event, [
        'ocorrido_em',
        'occurred_at',
        'created_at',
        'updated_at',
        'em',
      ]),
      id: String(event.id || event.sequencia || index),
      label: firstValue(
        event,
        ['label', 'tipo', 'event_type', 'action', 'status_novo'],
        'Atualização registrada'
      ),
    }))
    .slice(0, 3)
);

const submit = async () => {
  if (!canCreate.value) return;
  await model.create();
};
</script>

<template>
  <main
    data-imudei-visual-id="finance-create-root"
    class="mx-auto w-full max-w-[1328px] px-7 pb-24 pt-[106px] text-[#101958]"
  >
    <header
      data-imudei-visual-id="finance-create-header"
      class="flex min-h-[40px] items-start justify-between gap-6"
    >
      <div>
        <div class="flex items-center gap-4">
          <h1 class="text-[24px] font-bold tracking-[-0.03em]">Novo lançamento</h1>
          <span class="flex items-center gap-2 text-[8px] text-[#69759c]">
            <i class="i-lucide-cloud-off size-4 text-[#5a24c7]" />
            Criação depende de uma rota disponível na API financeira.
          </span>
        </div>
      </div>
      <span
        class="rounded-full bg-violet-50 px-3 py-1.5 text-[8px] font-semibold text-[#5a24c7]"
      >
        {{ canCreate ? 'API de criação disponível' : 'Criação indisponível' }}
      </span>
    </header>

    <section class="mt-3 grid grid-cols-[minmax(0,1fr)_310px] gap-6">
      <section class="min-w-0">
        <article
          data-imudei-visual-id="finance-create-form"
          class="min-h-[770px] overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white"
        >
          <nav class="grid grid-cols-5 border-b border-[#eeeaf4] px-4 py-3">
            <span
              v-for="(stage, index) in stages"
              :key="stage"
              class="relative flex items-center gap-3 text-[9px]"
              :class="index === 0 ? 'font-bold text-[#4d18bd]' : 'text-[#7a85a6]'"
            >
              <i
                class="z-10 grid size-7 place-items-center rounded-full border text-[10px]"
                :class="
                  index === 0
                    ? 'border-[#4d18bd] bg-[#4d18bd] text-white'
                    : 'border-[#dfe2eb] bg-white text-[#7a85a6]'
                "
              >
                {{ index + 1 }}
              </i>
              <strong>{{ stage }}</strong>
              <i
                v-if="index < stages.length - 1"
                class="absolute left-[75%] top-3.5 h-px w-1/3 bg-[#ebe8f3]"
              />
            </span>
          </nav>

          <section class="p-5">
            <header>
              <h2 class="text-[14px] font-bold">Escolha o tipo de lançamento</h2>
              <p class="mt-1 text-[9px] text-[#657298]">
                A seleção permanece indisponível até que a API disponibilize um contrato de criação.
              </p>
            </header>

            <div class="mt-5 grid grid-cols-3 gap-4">
              <article
                v-for="choice in creationChoices"
                :key="choice.title"
                class="min-h-[150px] rounded-xl border border-[#e7e2ef] bg-[#fdfcff] p-4 opacity-80"
                aria-disabled="true"
              >
                <span class="flex items-center justify-between">
                  <i class="grid size-10 place-items-center rounded-lg bg-violet-100 text-[#4d18bd]">
                    <i :class="choice.icon" class="size-5" />
                  </i>
                  <i class="i-lucide-lock-keyhole size-4 text-[#a2a9bc]" />
                </span>
                <strong class="mt-4 block text-[10px]">{{ choice.title }}</strong>
                <small class="mt-2 block text-[8px] leading-4 text-[#67739b]">
                  {{ choice.description }}
                </small>
              </article>
            </div>

            <slot name="creation-unavailable" :message="unavailableMessage">
              <p
                v-if="!canCreate"
                class="sr-only"
              >
                {{ unavailableMessage }}
              </p>
            </slot>

            <section
              v-if="runtimeFields.length"
              class="mt-5 rounded-xl border border-[#e8e4ef] bg-[#fcfbff] p-4"
            >
              <header>
                <h3 class="text-[11px] font-bold">Campos retornados pela API</h3>
                <p class="mt-1 text-[8px] text-[#69759c]">
                  Os valores abaixo são enviados somente se a ação de criação estiver disponível.
                </p>
              </header>
              <div class="mt-4 grid grid-cols-2 gap-3">
                <label v-for="field in runtimeFields" :key="field.key" class="block text-[8px] font-semibold">
                  {{ field.label }}
                  <select
                    v-if="field.type === 'select'"
                    v-model="model.form[field.key]"
                    class="mt-1.5 h-9 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[9px] font-normal outline-none focus:border-[#805ce1] disabled:cursor-not-allowed disabled:bg-[#f6f5f8]"
                    :disabled="!canCreate"
                  >
                    <option v-for="option in field.options" :key="option" :value="option">
                      {{ option }}
                    </option>
                  </select>
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    v-model="model.form[field.key]"
                    class="mt-1.5 min-h-[76px] w-full rounded-lg border border-[#ded9e9] bg-white px-3 py-2 text-[9px] font-normal outline-none focus:border-[#805ce1] disabled:cursor-not-allowed disabled:bg-[#f6f5f8]"
                    :disabled="!canCreate"
                  />
                  <input
                    v-else
                    v-model="model.form[field.key]"
                    class="mt-1.5 h-9 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[9px] font-normal outline-none focus:border-[#805ce1] disabled:cursor-not-allowed disabled:bg-[#f6f5f8]"
                    :disabled="!canCreate"
                    :type="field.type || 'text'"
                  />
                </label>
              </div>
            </section>

            <section class="mt-5">
              <header class="flex items-center justify-between gap-3">
                <span>
                  <h3 class="text-[11px] font-bold">Lançamentos existentes</h3>
                  <p class="mt-1 text-[8px] text-[#69759c]">
                    Contexto retornado pela API financeira da conta atual.
                  </p>
                </span>
                <span class="rounded-full bg-[#f3efff] px-2.5 py-1 text-[8px] font-bold text-[#5a24c7]">
                  {{ records.length }} retornados
                </span>
              </header>

              <label class="-mt-[79px] flex h-10 items-center gap-3 rounded-lg border border-[#e5e1eb] px-3 text-[#69759c]">
                <i class="i-lucide-search size-4" />
                <input
                  v-model="searchQuery"
                  class="min-w-0 flex-1 bg-transparent text-[9px] text-[#101958] outline-none placeholder:text-[#9aa2b9]"
                  placeholder="Buscar por lançamento, responsável ou imóvel"
                  type="search"
                />
              </label>

              <div class="mt-4 overflow-hidden rounded-xl border border-[#ece9f2]">
                <div class="grid grid-cols-[1.2fr_.95fr_.65fr_.8fr_.75fr] gap-3 bg-[#fcfbff] px-4 py-2 text-[7px] font-bold text-[#69759c]">
                  <span>Lançamento</span>
                  <span>Responsável</span>
                  <span>Vencimento</span>
                  <span>Valor</span>
                  <span>Status</span>
                </div>
                <p v-if="loading" class="px-4 py-6 text-center text-[8px] text-[#69759c]">
                  Carregando lançamentos retornados pela API…
                </p>
                <p v-else-if="!existingLaunches.length" class="px-4 py-6 text-center text-[8px] text-[#69759c]">
                  Nenhum lançamento corresponde aos dados retornados.
                </p>
                <template v-else>
                  <div
                    v-for="launch in existingLaunches"
                    :key="launch.id"
                    class="grid min-h-[56px] grid-cols-[1.2fr_.95fr_.65fr_.8fr_.75fr] items-center gap-3 border-t border-[#eeeaf4] px-4 text-[8px]"
                  >
                    <span class="flex min-w-0 items-center gap-2">
                      <i class="grid size-8 shrink-0 place-items-center rounded-lg bg-violet-50 text-[#5a24c7]">
                        <i class="i-lucide-building-2 size-4" />
                      </i>
                      <span class="min-w-0">
                        <strong class="block truncate text-[9px] text-[#293ea1]">{{ launch.reference || `#${launch.id}` }}</strong>
                        <small class="block truncate text-[7px] text-[#69759c]">{{ launch.title }}</small>
                      </span>
                    </span>
                    <span class="flex min-w-0 items-center gap-2">
                      <i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">
                        {{ initials(launch.manager) }}
                      </i>
                      <span class="truncate">{{ launch.manager || 'Não informado' }}</span>
                    </span>
                    <span>{{ formatDate(launch.scheduledAt) }}</span>
                    <strong>{{ launch.amount }}</strong>
                    <span class="w-fit rounded-md px-2 py-1 text-[7px] font-semibold" :class="statusTone(launch.status)">
                      {{ launch.status || 'Não informado' }}
                    </span>
                  </div>
                  <div
                    v-for="slot in Math.max(0, 5 - existingLaunches.length)"
                    :key="`launch-unavailable-${slot}`"
                    class="grid min-h-[56px] grid-cols-[1.2fr_.95fr_.65fr_.8fr_.75fr] items-center gap-3 border-t border-[#eeeaf4] px-4 text-[8px] text-[#8490ad]"
                  >
                    <span class="flex items-center gap-2">
                      <i class="grid size-8 place-items-center rounded-lg bg-[#faf9fc] text-[#a2a9bc]">
                        <i class="i-lucide-circle-slash-2 size-4" />
                      </i>
                      <span>
                        <strong class="block text-[8px] font-medium">Lançamento indisponível</strong>
                        <small class="block text-[7px]">Não retornado pela API</small>
                      </span>
                    </span>
                    <span>—</span>
                    <span>—</span>
                    <span>—</span>
                    <span class="w-fit rounded-md bg-[#f5f3f8] px-2 py-1 text-[7px]">Sem dados</span>
                  </div>
                </template>
              </div>
            </section>
          </section>
        </article>
      </section>

      <aside data-imudei-visual-id="finance-create-sidebar" class="-mt-7 space-y-4">
        <article class="min-h-[186px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[11px] font-bold">Resumo do lançamento</h2>
          <dl class="mt-5 space-y-4 text-[9px]">
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2 text-[#66739b]"><i class="i-lucide-arrow-down-to-line size-3.5" />Tipo</dt>
              <dd class="text-right font-semibold">Não definido</dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2 text-[#66739b]"><i class="i-lucide-link size-3.5" />Origem</dt>
              <dd class="text-right font-semibold">Não vinculada</dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2 text-[#66739b]"><i class="i-lucide-circle-dot size-3.5" />Status</dt>
              <dd><span class="rounded-md bg-violet-100 px-2 py-1 text-[8px] font-semibold text-[#5a24c7]">Indisponível</span></dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2 text-[#66739b]"><i class="i-lucide-badge-plus size-3.5" />Pontuação</dt>
              <dd><span class="rounded-md bg-[#f4f2f7] px-2 py-1 text-[8px] font-semibold text-[#7b849c]">Indisponível</span></dd>
            </div>
          </dl>
        </article>

        <article class="min-h-[270px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[11px] font-bold">Progresso de pontos</h2>
          <div class="mt-4 flex items-center gap-4">
            <span class="grid size-[74px] place-items-center rounded-full border-[7px] border-[#eceaf3] text-center text-[8px] text-[#7a85a6]">
              Dados<br />indisponíveis
            </span>
            <p class="text-[8px] leading-4 text-[#66739b]">
              A API financeira não retornou pontuação para uma criação de lançamento.
            </p>
          </div>
          <div class="mt-5 border-t border-[#eeeaf4] pt-4">
            <h3 class="text-[9px] font-bold">Próximos marcos</h3>
            <p class="mt-3 text-[8px] leading-4 text-[#66739b]">
              Regras e bônus aparecem somente quando retornados pela API.
            </p>
          </div>
        </article>

        <article class="min-h-[214px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between">
            <h2 class="text-[11px] font-bold">Atividade auditável</h2>
            <span class="text-[8px] text-[#5a24c7]">API</span>
          </header>
          <p v-if="!auditEvents.length" class="mt-4 text-[8px] leading-4 text-[#66739b]">
            Nenhuma atividade retornada pela API financeira.
          </p>
          <div v-else>
            <div v-for="event in auditEvents" :key="event.id" class="mt-4 flex gap-2">
              <i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">
                {{ initials(event.actor) }}
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[8px]">{{ event.actor }}</strong>
                <small class="block text-[7px] text-[#66739b]">{{ event.label }}</small>
                <small class="block text-[7px] text-[#66739b]">{{ formatDateTime(event.date) }}</small>
              </span>
            </div>
          </div>
        </article>

        <article class="h-[98px] overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-br from-[#4e18b9] to-[#8a32dc] p-3 text-white">
          <header class="flex items-center justify-between">
            <h2 class="text-[11px] font-bold">Missão diária</h2>
            <span class="text-[8px] text-white/75">Indisponível</span>
          </header>
          <p class="mt-3 text-[8px] leading-3 text-white/90">
            A gamificação responsável só é exibida quando o backend envia regras e progresso verificáveis.
          </p>
          <div class="mt-3 h-1.5 rounded-full bg-white/25">
            <i class="block h-full w-0 rounded-full bg-white" />
          </div>
        </article>
      </aside>
    </section>

    <p
      v-if="error || model.notice.value"
      class="fixed bottom-[74px] right-7 z-50 max-w-sm rounded-xl border px-4 py-3 text-[9px] leading-4"
      :class="error ? 'border-rose-100 bg-rose-50 text-rose-600' : 'border-emerald-100 bg-emerald-50 text-emerald-700'"
    >
      {{ error || model.notice.value }}
    </p>

    <footer
      data-imudei-visual-id="finance-create-footer"
      class="fixed bottom-0 left-0 right-0 z-40 flex h-[68px] items-center gap-3 border-t border-[#e7e2ed] bg-white/95 px-8 pl-[230px] shadow-[0_-6px_22px_rgba(47,29,87,0.04)] backdrop-blur"
    >
      <button
        class="h-9 rounded-lg border border-[#a989ef] px-7 text-[9px] font-bold text-[#5b20d2]"
        type="button"
        @click="emit('navigate', 'dashboard')"
      >
        Cancelar
      </button>
      <span class="mx-auto flex items-center gap-2 text-[8px] text-[#647096]">
        <i class="i-lucide-shield-alert size-3.5 text-[#5a24c7]" />
        {{ canCreate ? 'Dados serão enviados somente pela API disponível.' : unavailableMessage }}
      </span>
      <button
        class="h-9 rounded-lg border border-[#d8ccff] px-6 text-[9px] font-bold text-[#806fa8] disabled:cursor-not-allowed disabled:bg-[#f7f5fa]"
        disabled
        type="button"
      >
        Salvar rascunho
      </button>
      <button
        class="flex h-9 items-center gap-3 rounded-lg bg-gradient-to-r from-[#6a2ee5] via-[#8145ef] to-[#e54cb3] px-7 text-[9px] font-bold text-white shadow-[0_6px_18px_rgba(112,45,218,0.22)] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="saving || !canCreate"
        type="button"
        @click="submit"
      >
        {{ canCreate ? (saving ? 'Criando…' : 'Criar lançamento') : 'Criação indisponível' }}
        <i :class="canCreate ? 'i-lucide-arrow-right' : 'i-lucide-lock-keyhole'" class="size-4" />
      </button>
    </footer>
  </main>
</template>
